import { useState, type FC } from "react";
import { bulkRevokeCertificates } from "../../stores/certificate_store";
import type { BulkRevocationResponse } from "../../../../shared/models/bulk_revocation";

export const RevokeAllModal: FC<{
  groupName: string;
  certificates: { deviceId: string; certvalidfrom: string }[];
  onClose: () => void;
  onComplete: (result: BulkRevocationResponse) => void;
}> = ({ groupName, certificates, onClose, onComplete }) => {
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkRevocationResponse | undefined>();

  const isConfirmed = confirmText === "REVOKE";

  const handleRevoke = async () => {
    if (!isConfirmed || certificates.length === 0) return;
    setError(undefined);
    setIsSubmitting(true);
    try {
      const res = await bulkRevokeCertificates(certificates);
      setResult(res);
      onComplete(res);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to revoke certificates. Please try again.";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vmx-modal" style={{ maxWidth: 480 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">Revoke All Certificates</div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div className="vmx-modal-body">
        {result !== undefined ? (
          <div>
            <p style={{ color: "var(--vmx-gray-600)", lineHeight: 1.6 }}>
              Bulk revocation complete for <strong>{groupName}</strong>.
            </p>
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: "var(--vmx-gray-50)",
                borderRadius: "var(--vmx-radius)",
                fontSize: "0.875rem",
                lineHeight: 1.6,
              }}
            >
              <div>
                Revoked: <strong>{result.revoked}</strong>
              </div>
              <div>
                Skipped: <strong>{result.skipped}</strong>
              </div>
              <div>
                Total: <strong>{result.total}</strong>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: "var(--vmx-gray-600)", lineHeight: 1.6 }}>
              You are about to revoke all active and expiring certificates for
              device group <strong>{groupName}</strong>.
            </p>
            <p
              style={{
                color: "var(--vmx-gray-600)",
                lineHeight: 1.6,
                marginTop: 8,
              }}
            >
              <strong>{certificates.length}</strong> certificate
              {certificates.length !== 1 ? "s" : ""} will be revoked. This
              action cannot be undone.
            </p>
            <div className="vmx-form-group" style={{ marginTop: 16 }}>
              <label className="vmx-label">
                Type <strong>REVOKE</strong> to confirm
              </label>
              <input
                className="vmx-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="REVOKE"
                autoFocus
              />
            </div>
            {error !== undefined && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.8125rem",
                  color: "var(--vmx-danger)",
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="vmx-modal-footer">
        {result !== undefined ? (
          <button className="vmx-btn vmx-btn-secondary" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="vmx-btn vmx-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="vmx-btn vmx-btn-danger"
              onClick={handleRevoke}
              disabled={
                !isConfirmed || certificates.length === 0 || isSubmitting
              }
            >
              {isSubmitting ? "Revoking..." : "Revoke All"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
